import React from "react";
import { createRoot } from "react-dom/client";
import "./assets/css/App.css";
import { BrowserRouter } from "react-router-dom";
import { ChakraProvider } from "@chakra-ui/react";
import theme from "./theme/theme";
import "@/src/assets/css/globals.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./providers/AuthProvider";
import { toast } from "@/src/hooks/use-toast";

// This client was previously constructed bare, so every screen invented its own
// retry behaviour and its own error handling — eight distinct idioms across nine
// views. These are defaults only: a per-call `onError` or `retry` still wins, so
// nothing here changes existing screen behaviour.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Down from react-query's default of 3. api.ts already turns a 403 into a
      // logout, and support_api's error bodies are not transient, so the extra
      // attempts only delayed the error the operator needs to see.
      // staleTime and refetchOnWindowFocus are deliberately left at their
      // defaults — changing data freshness on the payout screens is a
      // behaviour change, not a cleanup.
      retry: 1,
    },
    mutations: {
      // Non-idempotent money endpoints. Never retry a mutation automatically.
      retry: 0,
      onError: (error) => {
        toast({
          title: "Something went wrong",
          description:
            error instanceof Error
              ? error.message
              : "An unknown error occurred",
          variant: "destructive",
        });
      },
    },
  },
});

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <QueryClientProvider client={queryClient}>
    <ChakraProvider theme={theme}>
      <React.StrictMode>
        <BrowserRouter>
          <AuthProvider />
        </BrowserRouter>
      </React.StrictMode>
    </ChakraProvider>
  </QueryClientProvider>,
);
